/**
 * Case Operations Dashboard
 * =========================
 * Operations dashboard for case management with:
 * - Overview stat cards
 * - Team workload visualization
 * - Playbook distribution
 * - SLA performance metrics
 * - Resolution trends
 */

import React from 'react';
import {
  FolderOpen,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Users,
  ListChecks,
  TrendingUp,
  Download,
  RefreshCw,
  Loader2,
  BarChart3,
  FileText,
} from 'lucide-react';
import {
  useCaseOverview,
  useTeamWorkload,
  useCasesByPlaybook,
  useSLAPerformance,
  useResolutionTrends,
} from '../hooks/useCaseAnalytics';
import { useNavigate } from 'react-router-dom';

// Simple bar component for charts
function Bar({ value, maxValue, color = 'var(--color-primary)', label, count }) {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;

  return (
    <div style={styles.barItem}>
      <div style={styles.barLabel}>
        <span>{label}</span>
        <span style={styles.barCount}>{count}</span>
      </div>
      <div style={styles.barTrack}>
        <div
          style={{
            ...styles.barFill,
            width: `${Math.min(percentage, 100)}%`,
            background: color,
          }}
        />
      </div>
    </div>
  );
}

// Stat card component
function StatCard({ icon: Icon, label, value, trend, color = 'var(--text-primary)', onClick }) {
  return (
    <div style={{ ...styles.statCard, cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <div style={{ ...styles.statIcon, background: `${color}15`, color }}>
        <Icon size={20} />
      </div>
      <div style={styles.statInfo}>
        <div style={styles.statLabel}>{label}</div>
        <div style={{ ...styles.statValue, color }}>{value}</div>
        {trend && <div style={styles.statTrend}>{trend}</div>}
      </div>
    </div>
  );
}

export default function CaseOperations() {
  const navigate = useNavigate();

  const { data: overview, isLoading: loadingOverview, refetch: refetchOverview } = useCaseOverview();
  const { data: workload, isLoading: loadingWorkload } = useTeamWorkload();
  const { data: playbooks, isLoading: loadingPlaybooks } = useCasesByPlaybook();
  const { data: sla, isLoading: loadingSLA } = useSLAPerformance();
  const { data: trends, isLoading: loadingTrends } = useResolutionTrends(30);

  const isLoading = loadingOverview || loadingWorkload || loadingPlaybooks || loadingSLA || loadingTrends;

  const handleRefresh = () => {
    refetchOverview();
  };

  const handleExport = () => {
    // Build CSV content from current data
    const lines = [];
    const timestamp = new Date().toISOString().split('T')[0];

    // Overview section
    lines.push('CASE OPERATIONS REPORT');
    lines.push(`Generated: ${new Date().toLocaleString()}`);
    lines.push('');
    lines.push('OVERVIEW');
    lines.push('Metric,Value');
    lines.push(`To Resolve,${(overview?.total_open || 0) + (overview?.total_in_progress || 0)}`);
    lines.push(`Open,${overview?.total_open || 0}`);
    lines.push(`In Progress,${overview?.total_in_progress || 0}`);
    lines.push(`Escalated,${overview?.total_escalated || 0}`);
    lines.push(`Resolved Today,${overview?.total_resolved_today || 0}`);
    lines.push(`Resolved This Week,${overview?.total_resolved_week || 0}`);
    lines.push(`Overdue,${overview?.total_overdue || 0}`);
    lines.push(`SARs Filed,${overview?.total_sars_filed || 0}`);
    lines.push(`Avg Resolution (hours),${overview?.avg_resolution_hours || 'N/A'}`);

    // Workload section
    if (workload?.officers?.length > 0) {
      lines.push('');
      lines.push('TEAM WORKLOAD');
      lines.push('Officer,Email,Open,In Progress,Escalated,Resolved Today,Resolved Week,Avg Hours');
      workload.officers.forEach((o) => {
        lines.push(`"${o.name}","${o.email}",${o.open_cases},${o.in_progress_cases},${o.escalated_cases},${o.resolved_today},${o.resolved_week},${o.avg_resolution_hours || 'N/A'}`);
      });
    }

    // Playbook section
    if (playbooks?.playbooks?.length > 0) {
      lines.push('');
      lines.push('CASES BY PLAYBOOK');
      lines.push('Playbook,Open,In Progress,Resolved,Avg Hours');
      playbooks.playbooks.forEach((p) => {
        lines.push(`"${p.playbook_name}",${p.open_cases},${p.in_progress_cases},${p.resolved_cases},${p.avg_resolution_hours || 'N/A'}`);
      });
      if (playbooks.unassigned_cases > 0) {
        lines.push(`"No Playbook",${playbooks.unassigned_cases},0,0,N/A`);
      }
    }

    // SLA section
    if (sla) {
      lines.push('');
      lines.push('SLA PERFORMANCE');
      lines.push('Metric,Value');
      lines.push(`On-time Rate,${sla.on_time_rate?.toFixed(1) || 0}%`);
      lines.push(`Overdue Rate,${sla.overdue_rate?.toFixed(1) || 0}%`);
      lines.push(`Target Hours,${sla.target_hours || 24}`);
      lines.push(`Avg Resolution Hours,${sla.avg_resolution_hours || 'N/A'}`);
    }

    // Create and download file
    const csvContent = lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `case-operations-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Calculate max values for bars
  const maxWorkload = workload?.officers?.reduce((max, o) =>
    Math.max(max, o.open_cases + o.in_progress_cases), 0) || 1;
  const maxPlaybookCases = playbooks?.playbooks?.reduce((max, p) =>
    Math.max(max, p.open_cases + p.in_progress_cases), 0) || 1;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Case Operations</h1>
          <p style={styles.subtitle}>Monitor team performance and case metrics</p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.refreshBtn} onClick={handleRefresh} disabled={isLoading}>
            {isLoading ? <Loader2 size={16} style={styles.spinner} /> : <RefreshCw size={16} />}
            Refresh
          </button>
          <button style={styles.exportBtn} onClick={handleExport}>
            <Download size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div style={styles.statsGrid}>
        <StatCard
          icon={FolderOpen}
          label="To Resolve"
          value={loadingOverview ? '...' : (overview?.total_open || 0) + (overview?.total_in_progress || 0)}
          color="var(--color-warning)"
          onClick={() => navigate('/cases?filter=open')}
        />
        <StatCard
          icon={Clock}
          label="Awaiting Info"
          value={loadingOverview ? '...' : overview?.total_in_progress || 0}
          color="var(--color-info)"
        />
        <StatCard
          icon={AlertTriangle}
          label="Escalated"
          value={loadingOverview ? '...' : overview?.total_escalated || 0}
          color="var(--color-danger)"
          onClick={() => navigate('/cases?filter=escalated')}
        />
        <StatCard
          icon={CheckCircle2}
          label="Resolved Today"
          value={loadingOverview ? '...' : overview?.total_resolved_today || 0}
          trend={overview?.total_resolved_week ? `${overview.total_resolved_week} this week` : undefined}
          color="var(--color-success)"
        />
        <StatCard
          icon={FileText}
          label="SARs Filed"
          value={loadingOverview ? '...' : overview?.total_sars_filed || 0}
          color="var(--color-warning)"
        />
        <StatCard
          icon={AlertTriangle}
          label="Overdue"
          value={loadingOverview ? '...' : overview?.total_overdue || 0}
          color="var(--color-danger)"
        />
      </div>

      {/* Main Content Grid */}
      <div style={styles.mainGrid}>
        {/* Team Workload */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <Users size={18} />
            <h3 style={styles.cardTitle}>Workload by Officer</h3>
          </div>
          <div style={styles.cardContent}>
            {loadingWorkload ? (
              <div style={styles.loading}>
                <Loader2 size={20} style={styles.spinner} />
                <span>Loading workload...</span>
              </div>
            ) : workload?.officers?.length === 0 ? (
              <div style={styles.empty}>No officers with assigned cases</div>
            ) : (
              workload?.officers?.slice(0, 8).map((officer) => (
                <Bar
                  key={officer.user_id}
                  label={officer.name}
                  value={officer.open_cases + officer.in_progress_cases}
                  maxValue={maxWorkload}
                  count={`${officer.open_cases + officer.in_progress_cases} cases`}
                  color={
                    officer.open_cases + officer.in_progress_cases > 10
                      ? 'var(--color-danger)'
                      : officer.open_cases + officer.in_progress_cases > 5
                      ? 'var(--color-warning)'
                      : 'var(--color-success)'
                  }
                />
              ))
            )}
          </div>
        </div>

        {/* Cases by Playbook */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <ListChecks size={18} />
            <h3 style={styles.cardTitle}>Open by Playbook</h3>
          </div>
          <div style={styles.cardContent}>
            {loadingPlaybooks ? (
              <div style={styles.loading}>
                <Loader2 size={20} style={styles.spinner} />
                <span>Loading playbooks...</span>
              </div>
            ) : playbooks?.playbooks?.length === 0 ? (
              <div style={styles.empty}>No cases with playbooks</div>
            ) : (
              <>
                {playbooks?.playbooks?.map((pb) => (
                  <Bar
                    key={pb.playbook_id}
                    label={pb.playbook_name}
                    value={pb.open_cases + pb.in_progress_cases}
                    maxValue={maxPlaybookCases}
                    count={`${pb.open_cases + pb.in_progress_cases} open`}
                    color="var(--color-primary)"
                  />
                ))}
                {playbooks?.unassigned_cases > 0 && (
                  <Bar
                    label="No Playbook"
                    value={playbooks.unassigned_cases}
                    maxValue={maxPlaybookCases}
                    count={`${playbooks.unassigned_cases} cases`}
                    color="var(--text-muted)"
                  />
                )}
              </>
            )}
          </div>
        </div>

        {/* SLA Performance */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <TrendingUp size={18} />
            <h3 style={styles.cardTitle}>SLA Performance</h3>
          </div>
          <div style={styles.cardContent}>
            {loadingSLA ? (
              <div style={styles.loading}>
                <Loader2 size={20} style={styles.spinner} />
                <span>Loading SLA data...</span>
              </div>
            ) : (
              <div style={styles.slaContent}>
                <div style={styles.slaMetric}>
                  <div style={styles.slaLabel}>On-time</div>
                  <div style={styles.slaBar}>
                    <div
                      style={{
                        ...styles.slaFill,
                        width: `${sla?.on_time_rate || 0}%`,
                        background: 'var(--color-success)',
                      }}
                    />
                  </div>
                  <div style={{ ...styles.slaValue, color: 'var(--color-success)' }}>
                    {sla?.on_time_rate?.toFixed(0) || 0}%
                  </div>
                </div>
                <div style={styles.slaMetric}>
                  <div style={styles.slaLabel}>Overdue</div>
                  <div style={styles.slaBar}>
                    <div
                      style={{
                        ...styles.slaFill,
                        width: `${sla?.overdue_rate || 0}%`,
                        background: 'var(--color-danger)',
                      }}
                    />
                  </div>
                  <div style={{ ...styles.slaValue, color: 'var(--color-danger)' }}>
                    {sla?.overdue_rate?.toFixed(0) || 0}%
                  </div>
                </div>
                <div style={styles.slaSummary}>
                  <div style={styles.slaSummaryItem}>
                    <span>Avg Resolution</span>
                    <strong>{sla?.avg_resolution_hours?.toFixed(1) || 'N/A'}h</strong>
                  </div>
                  <div style={styles.slaSummaryItem}>
                    <span>Target</span>
                    <strong>&lt; {sla?.target_hours || 24}h</strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Resolution Trend */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <BarChart3 size={18} />
            <h3 style={styles.cardTitle}>Resolution Trend (30 days)</h3>
          </div>
          <div style={styles.cardContent}>
            {loadingTrends ? (
              <div style={styles.loading}>
                <Loader2 size={20} style={styles.spinner} />
                <span>Loading trends...</span>
              </div>
            ) : (
              <div style={styles.trendChart}>
                {trends?.trends?.slice(-14).map((day, index) => {
                  const maxResolved = Math.max(...(trends?.trends?.slice(-14).map(d => d.resolved_count) || [1]));
                  const height = maxResolved > 0 ? (day.resolved_count / maxResolved) * 100 : 0;

                  return (
                    <div key={day.date} style={styles.trendBar}>
                      <div
                        style={{
                          ...styles.trendBarFill,
                          height: `${height}%`,
                        }}
                        title={`${day.date}: ${day.resolved_count} resolved`}
                      />
                      {index % 2 === 0 && (
                        <span style={styles.trendLabel}>
                          {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: '14px',
    color: 'var(--text-secondary)',
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    color: 'var(--text-primary)',
    fontSize: '14px',
    cursor: 'pointer',
  },
  exportBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    background: 'var(--color-primary)',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontSize: '14px',
    cursor: 'pointer',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px',
    background: 'var(--bg-primary)',
    border: '1px solid var(--border-color)',
    borderRadius: '12px',
    transition: 'all 0.15s',
  },
  statIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statInfo: {
    flex: 1,
  },
  statLabel: {
    fontSize: '13px',
    color: 'var(--text-secondary)',
    marginBottom: '4px',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 600,
  },
  statTrend: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    marginTop: '4px',
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '24px',
  },
  card: {
    background: 'var(--bg-primary)',
    border: '1px solid var(--border-color)',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)',
    color: 'var(--text-primary)',
  },
  cardTitle: {
    margin: 0,
    fontSize: '14px',
    fontWeight: 600,
  },
  cardContent: {
    padding: '20px',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    justifyContent: 'center',
    padding: '40px',
    color: 'var(--text-muted)',
    fontSize: '14px',
  },
  empty: {
    textAlign: 'center',
    padding: '40px',
    color: 'var(--text-muted)',
    fontSize: '14px',
  },
  spinner: {
    animation: 'spin 1s linear infinite',
  },
  barItem: {
    marginBottom: '16px',
  },
  barLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '6px',
    fontSize: '13px',
    color: 'var(--text-primary)',
  },
  barCount: {
    color: 'var(--text-secondary)',
    fontWeight: 500,
  },
  barTrack: {
    height: '8px',
    background: 'var(--bg-tertiary)',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  slaContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  slaMetric: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  slaLabel: {
    width: '80px',
    fontSize: '13px',
    color: 'var(--text-secondary)',
  },
  slaBar: {
    flex: 1,
    height: '12px',
    background: 'var(--bg-tertiary)',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  slaFill: {
    height: '100%',
    borderRadius: '6px',
    transition: 'width 0.3s ease',
  },
  slaValue: {
    width: '50px',
    textAlign: 'right',
    fontSize: '14px',
    fontWeight: 600,
  },
  slaSummary: {
    display: 'flex',
    gap: '24px',
    marginTop: '8px',
    paddingTop: '16px',
    borderTop: '1px solid var(--border-color)',
  },
  slaSummaryItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '13px',
    color: 'var(--text-secondary)',
  },
  trendChart: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '4px',
    height: '120px',
    paddingTop: '20px',
  },
  trendBar: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    position: 'relative',
  },
  trendBarFill: {
    width: '100%',
    maxWidth: '20px',
    background: 'var(--color-primary)',
    borderRadius: '4px 4px 0 0',
    marginTop: 'auto',
    transition: 'height 0.3s ease',
    minHeight: '4px',
  },
  trendLabel: {
    position: 'absolute',
    bottom: '-20px',
    fontSize: '10px',
    color: 'var(--text-muted)',
    whiteSpace: 'nowrap',
  },
};
